import { focusHaloFlush, MorphPanel, panelMotion, press, usePanelDismiss } from "@pax/ui";
import { Check, ChevronDown, Search } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useState } from "react";

/** Only one bubble is open at a time: opening one closes the rest. */
const bubbleOpened = "pax:bubble-opened";

const bubbleSurface =
  "rounded-2xl bg-background shadow-[0_4px_12px_rgb(0_0_0/0.06),0_24px_48px_-16px_rgb(0_0_0/0.2)] ring-1 ring-foreground/10";
const optionRow = `flex w-full items-center justify-between gap-4 rounded-xl px-3 py-2 text-left text-sm hover:bg-foreground/[0.07] ${panelMotion} ${focusHaloFlush}`;
export const markButton = `grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`;

export function Bubble({
  face,
  label,
  children,
  align = "left",
  icon = false,
  onOpenChange,
}: {
  face: ReactNode;
  label: string;
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
  icon?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);
  const rootRef = usePanelDismiss<HTMLSpanElement>({ open, onDismiss: close });
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const closeIfAnother = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== id) close();
    };
    window.addEventListener(bubbleOpened, closeIfAnother);
    return () => window.removeEventListener(bubbleOpened, closeIfAnother);
  }, [open, id, close]);
  const faceClass = icon
    ? markButton
    : `flex h-8 items-center gap-1.5 rounded-2xl px-3 text-sm font-medium whitespace-nowrap ${focusHaloFlush}`;
  return (
    <span
      ref={rootRef}
      data-open={open || undefined}
      className="relative inline-block shrink-0 align-middle"
    >
      <span aria-hidden className={`invisible ${faceClass}`}>
        {face}
      </span>
      <span
        className={`absolute top-0 ${align === "right" ? "right-0" : "left-0"} ${open ? "z-40" : "z-20"}`}
      >
        <MorphPanel
          view={open ? "expanded" : "face"}
          anchor={align === "right" ? "top-right" : "top-left"}
          className={open ? bubbleSurface : `rounded-2xl hover:bg-foreground/5 ${panelMotion}`}
        >
          {open ? (
            children(close)
          ) : (
            <button
              type="button"
              aria-label={label}
              aria-expanded={false}
              onClick={() => {
                window.dispatchEvent(new CustomEvent(bubbleOpened, { detail: id }));
                setOpen(true);
                onOpenChange?.(true);
              }}
              className={faceClass}
            >
              {face}
            </button>
          )}
        </MorphPanel>
      </span>
    </span>
  );
}

export function Picker<T extends string>({
  label,
  value,
  options,
  onChange,
  searchable = false,
  search,
  onSearch,
  placeholder = "Search",
  onWrite,
  writeLabel,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; detail?: string }[];
  onChange: (value: T) => void;
  searchable?: boolean;
  search?: string;
  onSearch?: (value: string) => void;
  placeholder?: string;
  onWrite?: (value: string) => void;
  writeLabel?: (value: string) => string;
}) {
  const [query, setQuery] = useState("");
  const typed = search ?? query;
  const shown = onSearch
    ? options
    : options.filter(option =>
        `${option.label} ${option.detail ?? ""}`.toLowerCase().includes(typed.toLowerCase()),
      );
  const selected = options.find(option => option.value === value);
  return (
    <Bubble
      label={label}
      face={
        <>
          <span className="max-w-64 truncate">{selected?.label ?? label}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </>
      }
    >
      {close => (
        <div className={`${searchable ? "w-80" : "w-64"} max-w-[calc(100vw-3rem)] p-1`}>
          {searchable && (
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                // biome-ignore lint/a11y/noAutofocus: opening the search picker starts a search
                autoFocus
                type="search"
                aria-label={placeholder}
                placeholder={placeholder}
                value={typed}
                onChange={event => (onSearch ?? setQuery)(event.target.value)}
                className="h-9 w-full rounded-xl bg-foreground/5 pl-9 pr-3 text-sm outline-none"
              />
            </label>
          )}
          <div className="scroll-fade max-h-72 overflow-y-auto">
            {shown.map(option => (
              <button
                type="button"
                key={option.value}
                className={optionRow}
                aria-pressed={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  close();
                  setQuery("");
                }}
              >
                <span className="min-w-0">
                  <span className={`block truncate ${option.value === value ? "font-medium" : ""}`}>
                    {option.label}
                  </span>
                  {option.detail && (
                    <span className="block text-xs text-muted-foreground">{option.detail}</span>
                  )}
                </span>
                {option.value === value && <Check className="size-3.5 shrink-0" />}
              </button>
            ))}
            {shown.length === 0 && (
              <p className="px-3 py-4 text-sm text-muted-foreground">No matches.</p>
            )}
            {typed.trim() &&
              onWrite &&
              !options.some(
                option => option.label.toLowerCase() === typed.trim().toLowerCase(),
              ) && (
                <button
                  type="button"
                  className={optionRow}
                  onClick={() => {
                    onWrite(typed.trim());
                    close();
                  }}
                >
                  {writeLabel?.(typed.trim()) ?? `Use “${typed.trim()}”`}
                </button>
              )}
          </div>
        </div>
      )}
    </Bubble>
  );
}
